import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { hasVaultKey } from "@/hooks/useKeyVault";
import { useAllNews } from "@/hooks/useNews";
import { formatDate } from "@/lib/utils";
import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";

type TradingViewWidgetEmbedProps = {
  ariaLabel: string;
  className?: string;
  config: Record<string, unknown>;
  fallbackHref: string;
  fallbackText: string;
  scriptSrc: string;
  widgetHeight?: string;
};

const tradingViewChartConfig = {
  allow_symbol_change: true,
  calendar: false,
  details: false,
  hide_side_toolbar: true,
  hide_top_toolbar: false,
  hide_legend: true,
  hide_volume: false,
  hotlist: false,
  interval: "15",
  locale: "en",
  save_image: true,
  style: "1",
  symbol: "BINANCE:BTCUSDT",
  theme: "dark",
  timezone: "Etc/UTC",
  backgroundColor: "#0F0F0F",
  gridColor: "rgba(242, 242, 242, 0.06)",
  watchlist: [],
  withdateranges: false,
  compareSymbols: [],
  show_popup_button: true,
  popup_height: "650",
  popup_width: "1000",
  studies: [
    "STD;MACD",
    "STD;Bollinger_Bands",
    "STD;MA%1Cross",
  ],
  autosize: true,
};

const tradingViewMarketOverviewConfig = {
  colorTheme: "dark",
  dateRange: "1D",
  locale: "en",
  largeChartUrl: "",
  isTransparent: true,
  showFloatingTooltip: false,
  plotLineColorGrowing: "rgba(41, 98, 255, 1)",
  plotLineColorFalling: "rgba(41, 98, 255, 1)",
  gridLineColor: "rgba(240, 243, 250, 0)",
  scaleFontColor: "#DBDBDB",
  belowLineFillColorGrowing: "rgba(41, 98, 255, 0.12)",
  belowLineFillColorFalling: "rgba(41, 98, 255, 0.12)",
  belowLineFillColorGrowingBottom: "rgba(41, 98, 255, 0)",
  belowLineFillColorFallingBottom: "rgba(41, 98, 255, 0)",
  symbolActiveColor: "rgba(41, 98, 255, 0.12)",
  tabs: [
    {
      title: "Indices",
      symbols: [
        { s: "FOREXCOM:SPXUSD", d: "S&P 500 Index" },
        { s: "FOREXCOM:NSXUSD", d: "US 100 Cash CFD" },
        { s: "FOREXCOM:DJI", d: "Dow Jones Industrial Average Index" },
        { s: "INDEX:NKY", d: "Japan 225" },
        { s: "INDEX:DEU40", d: "DAX Index" },
        { s: "FOREXCOM:UKXGBP", d: "FTSE 100 Index" },
      ],
      originalTitle: "Indices",
    },
    {
      title: "Commodities",
      symbols: [
        { s: "TVC:USOIL", d: "WTI Crude Oil" },
        { s: "TVC:UKOIL", d: "Brent Crude Oil" },
        { s: "TVC:GOLD", d: "Gold" },
        { s: "TVC:SILVER", d: "Silver" },
        { s: "TVC:PLATINUM", d: "Platinum" },
      ],
      originalTitle: "Commodities",
    },
    {
      title: "Bonds",
      symbols: [
        { s: "EUREX:FGBL1!", d: "Euro Bund" },
        { s: "EUREX:FBTP1!", d: "Euro BTP" },
        { s: "EUREX:FGBM1!", d: "Euro BOBL" },
      ],
      originalTitle: "Bonds",
    },
    {
      title: "Forex",
      symbols: [
        { s: "FX:EURUSD", d: "EUR to USD" },
        { s: "FX:GBPUSD", d: "GBP to USD" },
        { s: "FX:USDJPY", d: "USD to JPY" },
        { s: "FX:USDCHF", d: "USD to CHF" },
        { s: "FX:AUDUSD", d: "AUD to USD" },
        { s: "FX:USDCAD", d: "USD to CAD" },
      ],
      originalTitle: "Forex",
    },
    {
      title: "Crypto",
      symbols: [
        {
          s: "BINANCE:BTCUSDT",
          d: "",
          "base-currency-logoid": "crypto/XTVCBTC",
          "currency-logoid": "crypto/XTVCUSDT",
        },
        {
          s: "BINANCE:ETHUSDT",
          d: "",
          "base-currency-logoid": "crypto/XTVCETH",
          "currency-logoid": "crypto/XTVCUSDT",
        },
        {
          s: "BINANCE:SOLUSDT",
          d: "",
          "base-currency-logoid": "crypto/XTVCSOL",
          "currency-logoid": "crypto/XTVCUSDT",
        },
        {
          s: "BINANCE:DOGEUSDT",
          d: "",
          "base-currency-logoid": "crypto/XTVCDOGE",
          "currency-logoid": "crypto/XTVCUSDT",
        },
        { s: "CRYPTOCAP:BTC.D", d: "", logoid: "crypto/XTVCBTC" },
        { s: "CRYPTOCAP:USDT.D", d: "", logoid: "crypto/XTVCUSDT" },
      ],
    },
  ],
  support_host: "https://www.tradingview.com",
  backgroundColor: "#0f0f0f",
  width: "100%",
  height: "100%",
  showSymbolLogo: true,
  showChart: true,
};

const tradingViewEconomicCalendarConfig = {
  colorTheme: "dark",
  isTransparent: true,
  locale: "en",
  countryFilter: "ar,au,br,ca,cn,fr,de,in,id,it,jp,kr,mx,ru,sa,za,tr,gb,us,eu",
  importanceFilter: "0,1",
  width: "100%",
  height: "100%",
};

const TradingViewWidgetEmbed: React.FC<TradingViewWidgetEmbedProps> = ({
  ariaLabel,
  className = "",
  config,
  fallbackHref,
  fallbackText,
  scriptSrc,
  widgetHeight = "100%",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = "";

    const widget = document.createElement("div");
    widget.className = "tradingview-widget-container__widget";
    widget.style.height = widgetHeight;
    widget.style.width = "100%";

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = scriptSrc;
    script.async = true;
    script.innerHTML = JSON.stringify(config, null, 2);

    container.appendChild(widget);
    container.appendChild(script);

    return () => {
      container.innerHTML = "";
    };
  }, [config, scriptSrc, widgetHeight]);

  return (
    <section className={`home-tv-panel ${className}`.trim()} aria-label={ariaLabel}>
      <div ref={containerRef} className="tradingview-widget-container home-tv-container">
        <div className="tradingview-widget-container__widget" />
      </div>
      <noscript>
        <a href={fallbackHref}>{fallbackText}</a>
      </noscript>
    </section>
  );
};

const Home: React.FC = () => {
  const { data: latestNews, isLoading: newsLoading } = useAllNews(1000);

  const hasBYOK = hasVaultKey("groq");

  const sourceCount = useMemo(() => {
    if (!latestNews) return 0;
    return new Set(latestNews.map((item) => item.provider)).size;
  }, [latestNews]);

  return (
    <>
      <Helmet>
        <title>Tylee Smart Markets — Market Intelligence Hub</title>
        <meta name="description" content="A clean market intelligence hub for live news, plain-English briefings, live signals, and TTMA analytics. News is free; deeper analysis is BYOK." />
        <link rel="canonical" href="https://tyl.ee/" />
        <meta property="og:title" content="Tylee Smart Markets — Market Intelligence Hub" />
        <meta property="og:description" content="Live news, market briefs, monitor signals, and TTMA analytics in one clean interface. News stays open; analytics are BYOK." />
        <meta property="og:url" content="https://tyl.ee/" />
        <meta name="twitter:title" content="Tylee Smart Markets — Market Intelligence Hub" />
        <meta name="twitter:description" content="Live news, market briefs, monitor signals, and TTMA analytics in one clean interface." />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <a href="#home-main" className="skip-link">Skip to main content</a>

        <AppHeader />

      <main id="home-main" className="home-main">
        <div className="home-panel-row">
          <TradingViewWidgetEmbed
            ariaLabel="TradingView BTCUSDT chart"
            className="home-tv-panel--chart"
            config={tradingViewChartConfig}
            fallbackHref="https://www.tradingview.com/chart/?symbol=BINANCE%3ABTCUSDT"
            fallbackText="Open BTCUSDT chart on TradingView"
            scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js"
            widgetHeight="calc(100% - 32px)"
          />
          <TradingViewWidgetEmbed
            ariaLabel="TradingView market overview"
            className="home-tv-panel--overview"
            config={tradingViewMarketOverviewConfig}
            fallbackHref="https://www.tradingview.com/markets/"
            fallbackText="Open markets overview on TradingView"
            scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js"
          />
          <TradingViewWidgetEmbed
            ariaLabel="TradingView economic calendar"
            className="home-tv-panel--economic-calendar"
            config={tradingViewEconomicCalendarConfig}
            fallbackHref="https://www.tradingview.com/economic-calendar/"
            fallbackText="Open economic calendar on TradingView"
            scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-events.js"
          />
        </div>

        <section className="home-status-strip" aria-label="Session status">
          <div>
            <span>Access</span>
            <strong>{hasBYOK ? "BYOK" : "Guest"}</strong>
          </div>
          <div>
            <span>BYOK Vault</span>
            <strong>{hasBYOK ? "Active" : "Not set"}</strong>
          </div>
          <div>
            <span>Sources</span>
            <strong>{sourceCount || "--"}</strong>
          </div>
          <div>
            <span>Analytics</span>
            <strong>{hasBYOK ? "Unlimited" : "4/hr Guest"}</strong>
          </div>
        </section>

        <section className="home-news-band" aria-labelledby="home-news-title">
          <h2 id="home-news-title" className="home-news-heading-title">Latest headlines</h2>
          <div className="home-news-heading">
            <div>
              <p className="home-kicker">
                NEWS FEED
                {latestNews && latestNews.length > 0 && (
                  <span className="home-news-count">{latestNews.length} HEADLINES</span>
                )}
              </p>
            </div>
            <Link href="/news" className="home-news-link">Open terminal</Link>
          </div>

          <div className="home-news-list" aria-live="polite">
            {newsLoading && (
              <div className="home-news-empty">Loading headlines...</div>
            )}
            {!newsLoading && latestNews?.length === 0 && (
              <div className="home-news-empty">No headlines available.</div>
            )}
            {latestNews?.map((item) => (
              <a
                key={item.id}
                className="home-news-row"
                href={item.link || `/news`}
                target={item.link ? "_blank" : undefined}
                rel={item.link ? "noopener noreferrer" : undefined}
              >
                <span className="home-news-provider">{item.provider}</span>
                <span className="home-news-title">{item.title}</span>
                <time dateTime={item.pubDate}>{formatDate(item.pubDate)}</time>
              </a>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      </div>
    </>
  );
};

export default Home;
